<svelte:options namespace="svg" />

<Note {isPreview} setPrintSvg="{(event) => setPrintSvg(event)}">
  <metadata
    id="svg-data"
    data-color="{$store.textColor.name.state}"
    data-font-family="{$store.fontFace.family.state}"
    data-font-size="{$store.fontFace.size.state}"
    data-font-weight="{$store.fontFace.weight.state}"
    data-alignment="{$store.format.state}">
  </metadata>

  {#if svg}
    <g data-test-server-svg>{@html svg}</g>
  {:else}
    {#if layout === 'photo'}
      <image
        bind:this={photoContainer}
        data-note-id="photo"
        data-test-note-id="photo"
        xlink:href="{$photo.photoSrc.state}"
        transform="translate({svgPadding} {svgPadding})"
        preserveAspectRatio="xMidYMid meet"
        width={lineLength}
        height={lineLength}>
      </image>
      <g
        bind:this={textContainer}
        data-note-id="message"
        data-test-note-id="message"
        transform="translate({svgPadding} {offsetTextPhoto})"></g>
    {:else}
      <g
        bind:this={textContainer}
        data-note-id="message"
        data-test-note-id="message"
        transform="translate({svgPadding} {offset})"></g>
    {/if}
  {/if}
</Note>

<script>
  import Note from './Note.html';
  import { valueOf } from 'microstates';
  import { asyncWrite } from '../lib/writer';
  import { PADDING, SVG_WIDTH, SVG_HEIGHT, LINE_LENGTH } from '../lib/constants';
  import { store, photo } from '../stores';
  import { onMount, afterUpdate, tick } from 'svelte';
  import { debounce } from '../lib/debounce';

  export let layout;
  export let isPreview = false;

  let offset = 20;
  let svgWidth = SVG_WIDTH;
  let svgHeight = SVG_HEIGHT;
  let lineLength = LINE_LENGTH;
  let svgPadding = PADDING;
  let photoContainer;
  let textContainer;
  const debouncedWrite = debounce(write);

  $: showTextButton = $store.showTextPrompt;
  $: showPhotoButton = $photo.showPhotoPrompt;
  $: hasText = $store.hasText;
  $: hasPhoto = $photo.hasPhoto;
  $: isSaving = $store.isSaving;
  $: text = $store.getText;
  $: photoUploadProgress = $photo.photoUploadProgress.state;
  $: svg = $store.svg.state;
  $: offsetTextPhoto = offset + lineLength / 2;
  $: if ($store.textContainer.state) {
    debouncedWrite(text);
  }

  onMount(async () => {
    if (svg) { return; }

    store.set($store.textContainer.set(textContainer));

    await write(text);
  });

  afterUpdate(() => {
    /* TODO - probably a more efficient way to do this.
     * set state here to ensure validation of text height
     * is called after the dom has a chance to update
     * the textContainer
     */

    /* TODO - might make sense to refactor some of this
     * to use the `tick` method.
     * https://v3.svelte.technology/tutorial/tick
     */
    store.set($store.validatePrintability());
  });

  function setPrintSvg(svg) {
    store.set($store.printSvg.set(svg));
  };

  async function typeset(text, { lineLengths, spaceChar, fontFace, format, center, withHyphenation, tolerance, textColor }) {
    if (!textContainer) { return; }

    while (textContainer.firstChild) {
      textContainer.removeChild(textContainer.firstChild);
    }

    if (text.length === 0) {
      /*
       * if we're erasing the text area, don't
       * bother typesetting, just clear out the
       * textContainer
       */
      return { textContainer };
    }

    return await asyncWrite({
      text,
      fontFamily: fontFace.family.state,
      fontSize: fontFace.size.state,
      fontColor: textColor.hex.state,
      format: format.state,
      center: center.state,
      spaceChar: spaceChar.state,
      withHyphenation: withHyphenation.state,
      lineLengths: valueOf(lineLengths),
      tolerance: tolerance.state,
      textContainer
    }).catch(async (err) => {
      if (err.message === 'Can not typeset with current tolerance.') {
        if ($store.tolerance.state > 20000) {
          /*
           * prevent infinite retries if we can't typset
           * by 20000 tolerance
           */
          throw err;
        }

        store.set($store.tolerance.set($store.tolerance.state + 1000).setText(text));
        return await write(text);
      } else {
        throw err;
      }
    });
  };

  async function write(text) {
    const result = await typeset(text, $store);

    if (result) {
      offset = result.offset ? result.offset : offset;
    }
  };
</script>
